import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { getMyDetails } from 'api/users/usersApi';

const ACCOUNT_PATH = '/account';

/**
 * After a successful login, fetches the current user's profile once. If
 * `contact_email` is missing, redirects to /account so the user is prompted
 * to fill it in. Once the email is verified present, stops re-checking for
 * the rest of the session.
 *
 * Mount once at the app shell level, inside BrowserRouter.
 */
export const useRequireContactEmail = (): void => {
  const { token, authenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!authenticated || !token || verified) return;

    let cancelled = false;
    (async () => {
      try {
        const profile = await getMyDetails(token);
        if (cancelled) return;
        if (
          profile?.contact_email &&
          profile?.first_name &&
          profile?.last_name
        ) {
          setVerified(true);
        } else if (location.pathname !== ACCOUNT_PATH) {
          navigate(ACCOUNT_PATH, { replace: true });
        }
      } catch {
        // Auth/network failures are surfaced by the global API interceptor.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authenticated, token, verified, location.pathname, navigate]);
};

/** Headless component that mounts `useRequireContactEmail` at the app shell level. */
export const RequireContactEmailGate = (): null => {
  useRequireContactEmail();
  return null;
};
